import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { trackPromise } from "react-promise-tracker";
import Spinner from '../../common/components/Spinner';
import { RiFileZipFill, RiFileExcel2Fill } from 'react-icons/ri';
import { FaPlusSquare, FaMinusSquare, FaTimes, FaExchangeAlt, FaSearch, FaExpandAlt, FaCompressAlt } from 'react-icons/fa';
import { styles } from './ReportStudiesStyles';
import { SOFTDATANODEJS } from '../../config';

const QuadrosModal = ({ estudo, onClose }) => {
    const [allQuadrosCollapsed, setAllQuadrosCollapsed] = useState(false);
    const [quadrosData, setQuadrosData] = useState([]);
    const [collapsedQuadros, setCollapsedQuadros] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    const [showQuadroCreate, setShowQuadroCreate] = useState(false);
    const [createQuadroData, setCreateQuadroData] = useState({
        nome: '', nomeMenu: '', ordem: 1, estado: 1, quadroPai: 0, tipoDados: 100 // 100 = HTML por defeito
    });

    const [showCreateImportResultModal, setShowCreateImportResultModal] = useState(false);
    const [createImportResult, setCreateImportResult] = useState(null);

    const [showQuadroView, setShowQuadroView] = useState(false);
    const [showQuadroEdit, setShowQuadroEdit] = useState(false);
    const [showQuadroDelete, setShowQuadroDelete] = useState(false);

    const [showImportModal, setShowImportModal] = useState(false);
    const [importQuadroTarget, setImportQuadroTarget] = useState(null);
    const [importFile, setImportFile] = useState(null);
    const [importResult, setImportResult] = useState(null);

    const [showReplaceModal, setShowReplaceModal] = useState(false);
    const [replaceQuadroTarget, setReplaceQuadroTarget] = useState(null);
    const [replaceFile, setReplaceFile] = useState(null);
    const [replaceSheetIndex, setReplaceSheetIndex] = useState(1);
    const [replaceResult, setReplaceResult] = useState(null);

    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // --- STATES: IMPORTAÇÃO DE ZIP INDIVIDUAL (HTML) ---
    const [showZipImportModal, setShowZipImportModal] = useState(false);
    const [zipImportTarget, setZipImportTarget] = useState(null);
    const [zipFile, setZipFile] = useState(null);
    const [zipImportResult, setZipImportResult] = useState(null);

    // --- STATES: IMPORTAÇÃO DE MÚLTIPLOS ZIPS (HTML PAI) ---
    const [showMultiZipImportModal, setShowMultiZipImportModal] = useState(false);
    const [multiZipImportTarget, setMultiZipImportTarget] = useState(null);
    const [multiZipFiles, setMultiZipFiles] = useState([]);
    const [multiZipImportResult, setMultiZipImportResult] = useState(null);

    const [selectedQuadro, setSelectedQuadro] = useState(null);
    const [editQuadroData, setEditQuadroData] = useState(null);
    const [quadroFile, setQuadroFile] = useState(null);
    const [activeTab, setActiveTab] = useState('geral');

    const fetchQuadros = useCallback(async () => {
        try {
            const response = await trackPromise(
                axios.get(`${SOFTDATANODEJS}/estudo/${estudo.id}/quadros`)
            );
            setQuadrosData(response.data);
        } catch (err) {
            setErrorMessage("Erro ao carregar quadros.");
            setShowErrorModal(true);
        }
    }, [estudo.id]);

    useEffect(() => {
        fetchQuadros();
    }, [estudo.id, fetchQuadros]);

    const mapTipoDados = (tipo) => {
        switch (tipo) { case 0: return "MENU"; case 1: return "TIPO1"; case 2: return "TIPO2"; case 3: return "TIPO3"; case 100: return "HTML"; default: return `TIPO${tipo}`; }
    };
    const getQuadroPath = (quadro) => {
        if (!quadro) return "";
        let path = [quadro.nome];
        let currentParentId = quadro.quadroPai;

        while (currentParentId && currentParentId !== 0) {
            const targetId = currentParentId;
            const parent = quadrosData.find(q => q.id === targetId);

            if (parent) {
                path.unshift(parent.nome);
                currentParentId = parent.quadroPai;
            } else {
                break;
            }
        }
        return path.join(" > ");
    };

    const toggleQuadroCollapse = (quadroId) => setCollapsedQuadros(prev => ({ ...prev, [quadroId]: !prev[quadroId] }));

    const handleToggleAllQuadros = () => {
        if (allQuadrosCollapsed) {
            setCollapsedQuadros({});
            setAllQuadrosCollapsed(false);
        } else {
            const newState = {};
            quadrosData.forEach(q => {
                if (quadrosData.some(child => child.quadroPai === q.id)) {
                    newState[q.id] = true;
                }
            });
            setCollapsedQuadros(newState);
            setAllQuadrosCollapsed(true);
        }
    };

    const buildRecursiveQuadros = (parentId = 0, level = 0) => {
        let result = [];
        const children = quadrosData.filter(q => (q.quadroPai === parentId || (parentId === 0 && !q.quadroPai)));
        children.sort((a, b) => a.ordem - b.ordem);
        children.forEach(child => {
            const hasChildren = quadrosData.some(q => q.quadroPai === child.id);
            result.push({ ...child, level, hasChildren });
            if (hasChildren && !collapsedQuadros[child.id]) {
                result = result.concat(buildRecursiveQuadros(child.id, level + 1));
            }
        });
        return result;
    };

    let quadrosToRender = buildRecursiveQuadros();
    if (searchTerm) {
        const lowerTerm = searchTerm.toLowerCase();
        quadrosToRender = quadrosToRender.filter(q =>
            q.nome.toLowerCase().includes(lowerTerm) ||
            (q.nomeMenu && q.nomeMenu.toLowerCase().includes(lowerTerm)) ||
            String(q.id).includes(lowerTerm)
        );
    }

    const handleQuadroAction = (quadro, action) => {
        if (action === 'view') { setSelectedQuadro(quadro); setShowQuadroView(true); }
        else if (action === 'delete') { setSelectedQuadro(quadro); setShowQuadroDelete(true); }
        else if (action === 'edit') { setEditQuadroData({ ...quadro }); setQuadroFile(null); setActiveTab('geral'); setShowQuadroEdit(true); }
        else if (action === 'import-excel') {
            setImportQuadroTarget(quadro);
            setImportFile(null);
            setImportResult(null);
            setShowImportModal(true);
        }
        else if (action === 'replace-excel') {
            setReplaceQuadroTarget(quadro);
            setReplaceFile(null);
            setReplaceSheetIndex(1);
            setReplaceResult(null);
            setShowReplaceModal(true);
        }
        else if (action === 'import-zip-single') {
            setZipImportTarget(quadro);
            setZipFile(null);
            setZipImportResult(null);
            setShowZipImportModal(true);
        }
        else if (action === 'import-zip-multi') {
            setMultiZipImportTarget(quadro);
            setMultiZipFiles([]);
            setMultiZipImportResult(null);
            setShowMultiZipImportModal(true);
        }
    };

    const saveQuadroCreate = async () => {
        if (!createQuadroData.nome) {
            setErrorMessage("O nome do quadro é obrigatório.");
            setShowErrorModal(true);
            return;
        }

        const isHtml = createQuadroData.tipoDados === 100;
        const isExcelTipo = [1, 2, 3].includes(createQuadroData.tipoDados);

        if (quadroFile) {
            const fileName = quadroFile.name.toLowerCase();

            if (isHtml && !fileName.endsWith('.zip')) {
                setErrorMessage("Para quadros HTML, o ficheiro deve ser ZIP (.zip).");
                setShowErrorModal(true);
                return;
            }

            if (isExcelTipo && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
                setErrorMessage("Para quadros TIPO1, TIPO2 ou TIPO3, o ficheiro deve ser Excel (.xlsx ou .xls).");
                setShowErrorModal(true);
                return;
            }
        }

        try {
            const formData = new FormData();
            formData.append('estudoId', estudo.id);
            formData.append('nome', createQuadroData.nome);
            formData.append('nomeMenu', createQuadroData.nomeMenu || createQuadroData.nome);
            formData.append('ordem', createQuadroData.ordem);
            formData.append('estado', createQuadroData.estado);
            formData.append('quadroPai', createQuadroData.quadroPai || 0);
            formData.append('tipoDados', createQuadroData.tipoDados);

            // ZIP HTML continua a ser enviado no /quadro
            if (isHtml && quadroFile) {
                formData.append('file', quadroFile);
            }

            const createResponse = await trackPromise(
                axios.post(`${SOFTDATANODEJS}/quadro`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                })
            );

            const novoQuadroId =
                createResponse.data?.id ||
                createResponse.data?.quadro?.id ||
                createResponse.data?.quadroId ||
                createResponse.data?.insertId ||
                createResponse.data?.result?.insertId ||
                createResponse.data?.data?.id;

            const novoQuadroNome = createQuadroData.nome;

            // Se não houver Excel, termina normalmente
            if (!isExcelTipo || !quadroFile) {
                fetchQuadros();
                setShowQuadroCreate(false);
                setQuadroFile(null);
                return;
            }

            // Se houver Excel, precisamos do ID do quadro criado
            if (!novoQuadroId) {
                fetchQuadros();
                setShowQuadroCreate(false);
                setQuadroFile(null);

                setCreateImportResult({
                    success: false,
                    quadroCriado: true,
                    quadroNome: novoQuadroNome,
                    message: `O quadro "${novoQuadroNome}" foi criado com sucesso, mas não foi possível obter o ID para importar o Excel.`,
                    detailMessage: "Verifique se o endpoint POST /quadro devolve o ID do quadro criado."
                });

                setShowCreateImportResultModal(true);
                return;
            }

            const excelFormData = new FormData();
            excelFormData.append('file', quadroFile);
            excelFormData.append('estudoId', estudo.id);

            // IMPORTANTE:
            // não enviar quadroPaiId = novoQuadroId,
            // porque isso cria um quadro filho.
            // Queremos atualizar o próprio quadro acabado de criar.
            excelFormData.append('targetQuadroId', novoQuadroId);

            // O pai real do quadro continua a ser o escolhido na modal.
            // Só é enviado por compatibilidade/contexto.
            excelFormData.append('quadroPaiId', createQuadroData.quadroPai || 0);

            try {
                const importResponse = await trackPromise(
                    axios.post(`${SOFTDATANODEJS}/estudo/import-excel`, excelFormData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    })
                );

                const tipoFinal = importResponse.data?.tipoDados;
                const tipoFinalLabel = tipoFinal ? mapTipoDados(tipoFinal) : null;

                const nomeFinal =
                    importResponse.data?.nomeQuadro ||
                    importResponse.data?.nome ||
                    novoQuadroNome;

                setCreateImportResult({
                    success: true,
                    quadroCriado: true,
                    quadroNome: nomeFinal,
                    message: tipoFinalLabel
                        ? `O quadro "${nomeFinal}" foi criado com sucesso e o Excel foi importado com sucesso como ${tipoFinalLabel}.`
                        : `O quadro "${nomeFinal}" foi criado com sucesso e o Excel foi importado com sucesso.`,
                    warnings: importResponse.data?.warnings || []
                });

            } catch (err) {
                const apiError = err.response?.data || {};

                setCreateImportResult({
                    success: false,
                    quadroCriado: true,
                    quadroNome: novoQuadroNome,
                    message: `O quadro "${novoQuadroNome}" foi criado com sucesso, mas o Excel contém erros e não foi importado corretamente.`,
                    detailMessage: apiError.error || apiError.message || "Erro na importação do Excel.",

                    imported: apiError.imported ?? 0,
                    skippedCount: apiError.skippedCount ?? 0,
                    skipped: apiError.skipped || [],
                    skippedBySheet: apiError.skippedBySheet || [],
                    skippedSummary: apiError.skippedSummary || [],
                    warnings: apiError.warnings || [],

                    sheetName: apiError.sheetName || null,
                    row: apiError.row || null,
                    col: apiError.col || null,
                    cell: apiError.cell || null
                });
            }

            fetchQuadros();
            setShowQuadroCreate(false);
            setQuadroFile(null);
            setShowCreateImportResultModal(true);

        } catch (err) {
            const msg =
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Erro ao criar quadro.";

            setErrorMessage(msg);
            setShowErrorModal(true);
        }
    };

    const saveQuadroEdit = async () => {
        if (!editQuadroData) return;
        const formData = new FormData();
        formData.append('nome', editQuadroData.nome);
        formData.append('nomeMenu', editQuadroData.nomeMenu);
        formData.append('ordem', editQuadroData.ordem);
        formData.append('estado', editQuadroData.estado);
        formData.append('quadroPai', editQuadroData.quadroPai || 0);
        formData.append('tipoDados', editQuadroData.tipoDados);
        if (quadroFile) formData.append('file', quadroFile);

        try {
            await trackPromise(
                axios.put(`${SOFTDATANODEJS}/quadro/${editQuadroData.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            );
            fetchQuadros();
            setShowQuadroEdit(false);
        } catch (err) {
            const msg = err.response?.data?.error || err.message || "Erro ao guardar as alterações.";
            setErrorMessage(msg);
            setShowErrorModal(true);
        }
    };

    const confirmDeleteQuadro = async () => {
        if (!selectedQuadro) return;
        try {
            await trackPromise(
                axios.delete(`${SOFTDATANODEJS}/quadro/${selectedQuadro.id}`)
            );
            fetchQuadros();
            setShowQuadroDelete(false);
        } catch (err) {
            setErrorMessage("Erro ao apagar o quadro.");
            setShowErrorModal(true);
        }
    };

    // --- SUBMITS DE EXCEL ---
    const handleImportSubmit = async () => {
        if (!importFile || !importQuadroTarget) return;
        const formData = new FormData();
        formData.append('file', importFile);
        formData.append('estudoId', estudo.id);
        formData.append('quadroPaiId', importQuadroTarget.id);

        try {
            const response = await trackPromise(
                axios.post(`${SOFTDATANODEJS}/estudo/import-excel`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            );
            const imported = response.data.imported ?? 0;

            setImportResult({
                success: true,
                message: imported === 1
                    ? 'Excel importado com sucesso. Foi importado 1 quadro.'
                    : `Excel importado com sucesso. Foram importados ${imported} quadros.`,
                imported,
                skippedCount: response.data.skippedCount ?? 0,
                warnings: response.data.warnings || []
            });
        } catch (err) {
            const apiError = err.response?.data || {};

            setImportResult({
                success: false,
                message: apiError.error || apiError.message || "Erro na importação",

                imported: apiError.imported ?? 0,
                skippedCount: apiError.skippedCount ?? 0,
                skipped: apiError.skipped || [],
                skippedBySheet: apiError.skippedBySheet || [],
                skippedSummary: apiError.skippedSummary || [],
                warnings: apiError.warnings || [],

                sheetName: apiError.sheetName || null,
                row: apiError.row || null,
                col: apiError.col || null,
                cell: apiError.cell || null
            });
        }
    };

    const closeImportModal = () => { setShowImportModal(false); if (importResult?.success) fetchQuadros(); setImportResult(null); };

    const handleReplaceSubmit = async () => {
        if (!replaceFile || !replaceQuadroTarget) return;

        const formData = new FormData();
        formData.append('file', replaceFile);
        formData.append('sheetIndex', replaceSheetIndex);

        try {
            const response = await trackPromise(
                axios.post(
                    `${SOFTDATANODEJS}/quadro/${replaceQuadroTarget.id}/replace`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                )
            );

            setReplaceResult({
                success: true,
                message: response.data.message,
                warnings: response.data.warnings || []
            });
        } catch (err) {
            const apiError = err.response?.data || {};

            setReplaceResult({
                success: false,

                message:
                    apiError.message ||
                    apiError.error ||
                    'Erro ao substituir quadro.',

                imported: apiError.imported ?? 0,
                validatedBeforeFailure: apiError.validatedBeforeFailure ?? null,

                skippedCount: apiError.skippedCount ?? 0,
                skippedSheetCount: apiError.skippedSheetCount ?? apiError.skippedCount ?? 0,
                skippedErrorCount: apiError.skippedErrorCount ?? 0,

                skipped: apiError.skipped || [],
                skippedBySheet: apiError.skippedBySheet || [],
                skippedSummary: apiError.skippedSummary || [],

                warnings: apiError.warnings || [],

                sheetName: apiError.sheetName || null,
                row: apiError.row ?? null,
                col: apiError.col ?? null,
                cell: apiError.cell || null
            });
        }
    };

    const closeReplaceModal = () => { setShowReplaceModal(false); if (replaceResult?.success) fetchQuadros(); setReplaceResult(null); };

    // --- SUBMIT DA IMPORTAÇÃO DE ZIP INDIVIDUAL ---
    const handleZipImportSubmit = async () => {
        if (!zipFile || !zipImportTarget) return;

        const formData = new FormData();
        formData.append('nome', zipImportTarget.nome);
        formData.append('nomeMenu', zipImportTarget.nomeMenu);
        formData.append('ordem', zipImportTarget.ordem);
        formData.append('estado', zipImportTarget.estado);
        formData.append('quadroPai', zipImportTarget.quadroPai || 0);
        formData.append('tipoDados', zipImportTarget.tipoDados);
        formData.append('file', zipFile);

        try {
            const response = await trackPromise(
                axios.put(`${SOFTDATANODEJS}/quadro/${zipImportTarget.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            );
            setZipImportResult({ success: true, message: response.data.message || "Ficheiro ZIP associado ao quadro com sucesso!" });
        } catch (err) {
            // CORREÇÃO AQUI: Mostra o erro real do SFTP/Node.js
            const apiError = err.response?.data || {};
            setZipImportResult({ success: false, message: apiError.error || "Erro de comunicação com o servidor. Verifique o SFTP." });
        }
    };

    // --- SUBMIT DA IMPORTAÇÃO DE ZIPS MÚLTIPLOS ---
    // --- SUBMIT DA IMPORTAÇÃO DE ZIPS MÚLTIPLOS ---
    const handleMultiZipImportSubmit = async () => {
        if (!multiZipFiles?.length || !multiZipImportTarget) return;

        const formData = new FormData();

        // ⚠️ IMPORTANTE: Os campos de texto TÊM de ser adicionados ANTES dos ficheiros!
        // Caso contrário, o backend não os consegue ler a tempo.
        formData.append('estudoId', estudo.id);
        formData.append('quadroPaiId', multiZipImportTarget.id);

        // Só depois anexamos os ficheiros ZIP
        multiZipFiles.forEach(file => formData.append('files', file));

        try {
            const response = await trackPromise(
                axios.post(`${SOFTDATANODEJS}/estudo/import-html-zips`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            );
            setMultiZipImportResult({ success: true, message: response.data.message || `${multiZipFiles.length} ficheiros ZIP importados com sucesso.` });
        } catch (err) {
            const apiError = err.response?.data || {};
            setMultiZipImportResult({ success: false, message: apiError.error || "Erro de comunicação com o servidor. Verifique o SFTP." });
        }
    };

    return (
        <div style={styles.modalOverlay}>
            <Spinner />

            <div style={styles.modalContentLarge}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #005c75', paddingBottom: '10px', marginBottom: '10px' }}>
                    <h2 style={{ margin: 0, color: '#333', fontSize: '18px' }}><span style={{ color: '#005c75', fontWeight: 'bold' }}>Quadros: </span> {estudo.nome}</h2>
                    <button style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }} onClick={onClose}><FaTimes /></button>
                </div>

                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ ...styles.searchBox, height: '34px', minHeight: '34px', maxHeight: '34px', boxSizing: 'border-box', margin: 0, padding: '0 8px' }}>
                        <FaSearch style={{ color: '#666', marginRight: '5px' }} />
                        <input
                            placeholder="Pesquisar Quadro (Nome, ID...)"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            style={{ ...styles.searchInput, margin: 0, height: '100%', boxSizing: 'border-box', backgroundColor: 'transparent' }}
                        />
                    </div>
                    <button
                        style={{ ...styles.footerBtn, height: '34px', minHeight: '34px', maxHeight: '34px', boxSizing: 'border-box', padding: '0 12px', display: 'flex', alignItems: 'center', gap: '6px', color: '#005c75', border: '1px solid #005c75', backgroundColor: '#e2f3f5', fontWeight: 'bold', margin: 0 }}
                        onClick={handleToggleAllQuadros}
                    >
                        {allQuadrosCollapsed ? <FaExpandAlt size={14} /> : <FaCompressAlt size={14} />}
                        {allQuadrosCollapsed ? 'Expandir Quadros' : 'Colapsar Quadros'}
                    </button>
                    <button
                        style={{
                            ...styles.footerBtn,
                            height: '34px',
                            boxSizing: 'border-box',
                            padding: '0 15px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: '#28a745',
                            color: '#fff',
                            border: 'none',
                            fontWeight: 'bold',
                            margin: 0,
                            whiteSpace: 'nowrap'
                        }}
                        onClick={() => {
                            setCreateQuadroData({ nome: '', nomeMenu: '', ordem: 1, estado: 1, quadroPai: 0, tipoDados: 100 });
                            setQuadroFile(null);
                            setShowQuadroCreate(true);
                        }}
                    >
                        <FaPlusSquare size={16} />
                        <span>Novo Quadro</span>
                    </button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #ccc' }}>
                    <table style={styles.table}>
                        <thead style={styles.theadSticky}>
                            <tr style={styles.theadRow}>
                                <th style={{ ...styles.th, textAlign: 'left' }}>Nome</th><th style={{ ...styles.th, textAlign: 'left' }}>Menu</th><th style={{ ...styles.th, width: '120px' }}>Código</th><th style={{ ...styles.th, width: '80px' }}>Tipo</th><th style={{ ...styles.th, width: '50px' }}>Ord.</th><th style={{ ...styles.th, width: '80px' }}>Estado</th><th style={{ ...styles.th, width: '220px' }}>Acções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {quadrosToRender.length === 0 ? (<tr><td colSpan="8" style={{ padding: '20px', textAlign: 'center', color: '#333' }}>Nenhum quadro encontrado.</td></tr>) : (
                                (() => {
                                    let zebraCounter = 0;

                                    return quadrosToRender.map((q, idx) => {
                                        const isMainParent = q.level === 0 || q.hasChildren;
                                        let trStyle = q.level === 0 ? styles.groupHeaderRow : {};

                                        if (q.level !== 0) {
                                            trStyle = zebraCounter % 2 === 0 ? styles.dataRow : styles.dataRowAlt;
                                            zebraCounter++;
                                        }

                                        const tdStyle = q.level === 0 ? styles.groupTd : styles.td;
                                        const indentPadding = q.level * 25 + (q.level === 0 ? 10 : 25);
                                        const isMenu = q.tipoDados === 0;

                                        return (
                                            <tr key={`${q.id}-${idx}`} style={trStyle}>
                                                <td style={{ ...tdStyle, textAlign: 'left' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: `${indentPadding}px` }}>
                                                        {q.hasChildren && !searchTerm && (
                                                            <span onClick={() => toggleQuadroCollapse(q.id)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: q.level === 0 ? '#fff' : '#005c75' }}>
                                                                {collapsedQuadros[q.id] ? <FaPlusSquare size={16} /> : <FaMinusSquare size={16} />}
                                                            </span>
                                                        )}
                                                        {q.level !== 0 && !q.hasChildren && !searchTerm && <span style={{ color: '#ccc' }}>↳</span>}
                                                        <span>{q.nome || "N/D"}</span>
                                                    </div>
                                                </td>
                                                <td style={tdStyle}>{q.nomeMenu}</td><td style={{ ...tdStyle, textAlign: 'center' }}>{q.id}</td><td style={{ ...tdStyle, textAlign: 'center' }}>{mapTipoDados(q.tipoDados)}</td><td style={{ ...tdStyle, textAlign: 'center' }}>{q.ordem}</td><td style={{ ...tdStyle, textAlign: 'center', color: q.level === 0 ? '#fff' : (q.estado === 1 ? '#28a745' : '#dc3545') }}>{q.estado === 1 ? 'ACTIVO' : 'INACTIVO'}</td>
                                                <td style={styles.tdActions}>
                                                    {/* Mostra o botão de substituir Excel para todos, EXCETO Menus(0), Dashboards(9) e HTML(100) */}
                                                    {![0, 9, 100].includes(q.tipoDados) && (
                                                        <button style={styles.iconBtn} onClick={() => handleQuadroAction(q, 'replace-excel')} title="Substituir Quadro">
                                                            <FaExchangeAlt style={{ color: '#E67E22' }} />
                                                        </button>
                                                    )}

                                                    {isMenu && (
                                                        <button style={styles.iconBtn} onClick={() => handleQuadroAction(q, 'import-excel')} title="Importar Excel">
                                                            <RiFileExcel2Fill style={{ color: '#1D6F42' }} />
                                                        </button>
                                                    )}

                                                    {/* REGRAS DO ZIP: SÓ APARECE EM QUADROS HTML */}
                                                    {q.tipoDados === 100 && (
                                                        isMainParent ? (
                                                            <button style={styles.iconBtn} onClick={() => handleQuadroAction(q, 'import-zip-multi')} title="Importar Múltiplos ZIPs (Neste Quadro Pai)">
                                                                <RiFileZipFill style={{ color: '#005c75' }} />
                                                            </button>
                                                        ) : (
                                                            <button style={styles.iconBtn} onClick={() => handleQuadroAction(q, 'import-zip-single')} title="Importar 1 ZIP (HTML)">
                                                                <RiFileZipFill style={{ color: '#17a2b8' }} />
                                                            </button>
                                                        )
                                                    )}

                                                    <button style={styles.iconBtn} onClick={() => handleQuadroAction(q, 'view')}>👁</button>
                                                    <button style={styles.iconBtn} onClick={() => handleQuadroAction(q, 'edit')}>✎</button>
                                                    <button style={styles.iconBtn} onClick={() => handleQuadroAction(q, 'delete')}>🗑</button>
                                                </td>
                                            </tr>
                                        );
                                    });
                                })()
                            )}
                        </tbody>
                    </table>
                </div>
                <div style={{ marginTop: '10px', textAlign: 'right' }}><button style={styles.footerBtn} onClick={onClose}>Fechar</button></div>
            </div>

            {/* --- MODAIS BÁSICAS EXISTENTES --- */}
            {showQuadroView && selectedQuadro && (<div style={styles.modalOverlay}>
                <div style={styles.modalContent}>
                    <h2 style={styles.modalTitle}>Detalhe Quadro</h2>
                    {/* EXIBIÇÃO DA HIERARQUIA */}
                    <div style={{ marginBottom: '15px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '4px', borderLeft: '4px solid #005c75', fontSize: '13px', color: '#555', textAlign: 'left' }}>
                        <strong>📍 Localização Atual:</strong><br />
                        <span style={{ color: '#005c75', fontWeight: 'bold' }}>{getQuadroPath(selectedQuadro)}</span>
                    </div>
                    <table style={styles.infoTable}><thead><tr style={styles.infoSectionHeader}><th colSpan="2">Informação</th></tr></thead><tbody><tr><td style={styles.infoLabel}>ID:</td><td style={styles.infoVal}>{selectedQuadro.id}</td></tr><tr><td style={styles.infoLabel}>Nome:</td><td style={styles.infoVal}>{selectedQuadro.nome}</td></tr><tr><td style={styles.infoLabel}>Menu:</td><td style={styles.infoVal}>{selectedQuadro.nomeMenu}</td></tr><tr><td style={styles.infoLabel}>Tipo:</td><td style={styles.infoVal}>{mapTipoDados(selectedQuadro.tipoDados)}</td></tr><tr><td style={styles.infoLabel}>Ordem:</td><td style={styles.infoVal}>{selectedQuadro.ordem}</td></tr><tr><td style={styles.infoLabel}>Estado:</td><td style={{ ...styles.infoVal, color: selectedQuadro.estado === 1 ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>{selectedQuadro.estado === 1 ? 'ACTIVO' : 'INACTIVO'}</td></tr>{selectedQuadro.tipoDados === 100 && (
                        <tr>
                            <td style={styles.infoLabel}>Ficheiro (Zip):</td>
                            <td style={styles.infoVal}>{selectedQuadro.ficheiroTexto || "Sem ficheiro associado"}</td>
                        </tr>
                    )}</tbody></table><div style={styles.modalFooter}><button style={styles.footerBtn} onClick={() => setShowQuadroView(false)}>Fechar</button></div></div></div>)}
            {showQuadroEdit && editQuadroData && (<div style={styles.modalOverlay}><div style={{ ...styles.modalContent, maxWidth: '800px' }}><h2 style={styles.modalTitle}>Editar Quadro</h2>

                {/* EXIBIÇÃO DA HIERARQUIA */}
                <div style={{ marginBottom: '15px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '4px', borderLeft: '4px solid #005c75', fontSize: '13px', color: '#555', textAlign: 'left' }}>
                    <strong>📍 Localização Atual:</strong><br />
                    <span style={{ color: '#005c75', fontWeight: 'bold' }}>{getQuadroPath(editQuadroData)}</span>
                </div>
                <div style={{ display: 'flex', borderBottom: '1px solid #ccc', marginBottom: 15 }}><div style={activeTab === 'geral' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('geral')}>Dados Gerais</div><div style={activeTab === 'config' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('config')}>Configurações</div></div>{activeTab === 'geral' ? (<div style={{ maxHeight: '60vh', overflowY: 'auto' }}><table style={styles.infoTable}><tbody><tr><td style={styles.infoLabel}>Nome:</td><td style={styles.infoVal}><input style={styles.modalInput} value={editQuadroData.nome} onChange={e => setEditQuadroData({ ...editQuadroData, nome: e.target.value })} /></td></tr><tr><td style={styles.infoLabel}>Pai:</td><td style={styles.infoVal}><select style={styles.modalInput} value={editQuadroData.quadroPai || 0} onChange={e => setEditQuadroData({ ...editQuadroData, quadroPai: Number(e.target.value) })}><option value="0">[Sem Pai]</option>{quadrosData.filter(x => x.id !== editQuadroData.id).map(x => <option key={x.id} value={x.id}>{x.nome}</option>)}</select></td></tr><tr><td style={styles.infoLabel}>ID:</td><td style={styles.infoVal}>{editQuadroData.id}</td></tr><tr><td style={styles.infoLabel}>Menu:</td><td style={styles.infoVal}><input style={styles.modalInput} value={editQuadroData.nomeMenu} onChange={e => setEditQuadroData({ ...editQuadroData, nomeMenu: e.target.value })} /></td></tr><tr><td style={styles.infoLabel}>Tipo:</td><td style={styles.infoVal}>{mapTipoDados(editQuadroData.tipoDados)}</td></tr><tr><td style={styles.infoLabel}>Ordem:</td><td style={styles.infoVal}><input type="number" style={{ ...styles.modalInput, width: 60 }} value={editQuadroData.ordem} onChange={e => setEditQuadroData({ ...editQuadroData, ordem: e.target.value })} /></td></tr><tr><td style={styles.infoLabel}>Estado:</td><td style={styles.infoVal}><label style={{ marginRight: 15, color: '#333', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><input type="radio" checked={editQuadroData.estado === 1} onChange={() => setEditQuadroData({ ...editQuadroData, estado: 1 })} style={{ marginRight: 5 }} /> Activo</label><label style={{ color: '#333', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><input type="radio" checked={editQuadroData.estado === 0} onChange={() =>
                    setEditQuadroData({ ...editQuadroData, estado: 0 })} style={{ marginRight: 5 }} /> Inactivo</label></td></tr>
                    {editQuadroData.tipoDados === 100 && (
                        <>
                            <tr>
                                <td style={styles.infoLabel}>Nome do Ficheiro:</td>
                                <td style={styles.infoVal}>
                                    <input
                                        style={styles.modalInput}
                                        value={editQuadroData.ficheiroTexto || ''}
                                        onChange={e => setEditQuadroData({ ...editQuadroData, ficheiroTexto: e.target.value })}
                                        placeholder="ex: 201250001.zip"
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.infoLabel}>Carregar / Substituir:</td>
                                <td style={styles.infoVal}>
                                    {/* Adicionamos uma altura mínima para igualar aos outros inputs e garantir o centro */}
                                    <div style={{ display: 'flex', alignItems: 'center', minHeight: '34px' }}>
                                        <label style={{ ...styles.uploadBtn, background: '#005c75', color: '#fff', border: 'none', margin: 0 }}>
                                            <RiFileZipFill style={{ marginRight: 5 }} /> Escolher Ficheiro
                                            <input type="file" accept=".zip" hidden onChange={e => {
                                                const f = e.target.files[0];
                                                setQuadroFile(f);
                                                if (f) setEditQuadroData({ ...editQuadroData, ficheiroTexto: f.name });
                                            }} />
                                        </label>

                                        <span style={{
                                            marginLeft: 10,
                                            fontSize: 14,
                                            color: '#333',
                                            display: 'flex',
                                            alignItems: 'center', // Garante o alinhamento médio do texto
                                            lineHeight: 'normal'
                                        }}>
                                            {quadroFile ? `A carregar: ${quadroFile.name}` : (editQuadroData.ficheiroTexto ? "Ficheiro atual mantido" : "Nenhum ficheiro selecionado")}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </>
                    )}
                </tbody></table></div>) : (<div style={{ padding: 20, textAlign: 'center', color: '#666' }}>Configurações avançadas indisponíveis no momento.</div>)}<div style={styles.modalFooter}><button style={{ ...styles.footerBtn, background: '#28a745', color: 'white' }} onClick={saveQuadroEdit}>Guardar</button><button style={styles.footerBtn} onClick={() => setShowQuadroEdit(false)}>Cancelar</button></div></div></div>)}
            {showQuadroDelete && selectedQuadro && (<div style={styles.modalOverlay}><div style={{ ...styles.modalContent, maxWidth: 450, textAlign: 'center' }}><h3 style={{ color: '#dc3545', marginTop: 0 }}>Apagar Quadro?</h3>
                {/* EXIBIÇÃO DA HIERARQUIA */}
                <div style={{ marginBottom: '15px', padding: '8px 12px', background: '#fff5f5', borderRadius: '4px', borderLeft: '4px solid #dc3545', fontSize: '13px', color: '#555', textAlign: 'left' }}>
                    <strong style={{ color: '#dc3545' }}>📍 Alvo a Eliminar:</strong><br />
                    <span style={{ color: '#dc3545', fontWeight: 'bold' }}>{getQuadroPath(selectedQuadro)}</span>
                </div>
                <p style={{ color: '#333' }}>Confirma a remoção de <strong>{selectedQuadro.nome}</strong>?</p><div style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '10px', borderRadius: '4px', fontSize: '14px', marginTop: '10px', marginBottom: '10px', border: '1px solid #ffeeba' }}><strong>⚠️ Atenção:</strong> Esta ação é irreversível. <br />Ao apagar este quadro, <strong>todos os seus filhos, netos e dependências</strong> serão também eliminados.</div><div style={styles.modalFooterCenter}><button style={{ ...styles.footerBtn, background: '#dc3545', color: 'white' }} onClick={confirmDeleteQuadro}>Apagar</button><button style={styles.footerBtn} onClick={() => setShowQuadroDelete(false)}>Cancelar</button></div></div></div>)}
            {showImportModal && importQuadroTarget && (
                <div style={styles.modalOverlay}>
                    <div style={{ ...styles.modalContent, maxWidth: '700px' }}>
                        <h2 style={{ ...styles.modalTitle, borderBottom: '2px solid #1D6F42' }}>
                            Importar Excel
                        </h2>

                        {!importResult ? (
                            <div style={{ padding: '15px' }}>
                                <p style={{ fontSize: '14px', color: '#333' }}>
                                    Importar quadros para: <strong>{importQuadroTarget.nome}</strong>
                                </p>

                                <div
                                    style={styles.dropZone}
                                    onClick={() => document.getElementById('quadro-excel-upload').click()}
                                >
                                    <RiFileExcel2Fill
                                        style={{
                                            color: '#1D6F42',
                                            fontSize: '40px',
                                            marginBottom: '10px'
                                        }}
                                    />

                                    <p
                                        style={{
                                            fontSize: '14px',
                                            color: '#1D6F42',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {importFile ? importFile.name : 'Clique para selecionar o ficheiro Excel'}
                                    </p>

                                    <input
                                        type="file"
                                        id="quadro-excel-upload"
                                        style={{ display: 'none' }}
                                        accept=".xlsx, .xls"
                                        onChange={(e) => setImportFile(e.target.files[0])}
                                    />
                                </div>

                                <div style={styles.modalFooter}>
                                    <button
                                        style={{
                                            ...styles.footerBtn,
                                            background: importFile ? '#1D6F42' : '#ccc',
                                            color: '#fff',
                                            border: 'none'
                                        }}
                                        disabled={!importFile}
                                        onClick={handleImportSubmit}
                                    >
                                        Processar
                                    </button>

                                    <button
                                        style={styles.footerBtn}
                                        onClick={() => setShowImportModal(false)}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div
                                style={{
                                    padding: '12px 16px',
                                    textAlign: 'center',
                                    maxHeight: '72vh',
                                    overflowY: 'auto',
                                    boxSizing: 'border-box'
                                }}
                            >
                                {importResult.success ? (
                                    <div style={{ color: 'green', fontSize: '16px' }}>
                                        <p style={{ fontSize: '40px' }}>✅</p>
                                        <strong>Sucesso!</strong>
                                        <p>{importResult.message}</p>
                                        {importResult.imported !== undefined && (
                                        <div
                                            style={{
                                                marginTop: '12px',
                                                padding: '10px',
                                                background: '#eaf7ea',
                                                border: '1px solid #badbcc',
                                                borderRadius: '4px',
                                                color: '#0f5132',
                                                fontSize: '14px',
                                                textAlign: 'left'
                                            }}
                                        >
                                            <div>
                                                <strong>Quadros importados:</strong>{' '}
                                                {importResult.imported}
                                            </div>

                                            {importResult.skippedCount > 0 && (
                                                <div>
                                                    <strong>Folhas ignoradas:</strong>{' '}
                                                    {importResult.skippedCount}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                        {importResult.warnings && importResult.warnings.length > 0 && (
                                            <div
                                                style={{
                                                    marginTop: '15px',
                                                    padding: '10px',
                                                    background: '#fff3cd',
                                                    color: '#856404',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ffeeba',
                                                    fontSize: '14px',
                                                    textAlign: 'left',
                                                    maxHeight: '100px',
                                                    overflowY: 'auto'
                                                }}
                                            >
                                                <strong>⚠️ Avisos na importação:</strong>

                                                <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                                                    {importResult.warnings.map((w, i) => (
                                                        <li key={i} style={{ marginBottom: '6px' }}>
                                                            {w}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ color: '#dc3545', fontSize: '16px' }}>
                                        <p style={{ fontSize: '40px' }}>❌</p>
                                        <strong>Erro na Importação</strong>

                                        <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {importResult.message}
                                        </p>

                                        {importResult.imported !== undefined && (
                                            <div
                                                style={{
                                                    marginTop: '12px',
                                                    padding: '10px',
                                                    background: '#fff3cd',
                                                    border: '1px solid #ffeeba',
                                                    borderRadius: '4px',
                                                    color: '#856404',
                                                    fontSize: '14px',
                                                    textAlign: 'left'
                                                }}
                                            >
                                                <div>
                                                    <strong>Quadros processados antes do erro:</strong>{' '}
                                                    {importResult.validatedBeforeFailure ?? importResult.imported ?? 0}
                                                </div>

                                                <div>
                                                    <strong>Folhas com erro:</strong>{' '}
                                                    {importResult.skippedSheetCount ?? importResult.skippedCount ?? 0}
                                                </div>

                                                <div>
                                                    <strong>Erros encontrados:</strong>{' '}
                                                    {importResult.skippedErrorCount ?? importResult.skipped?.length ?? 0}
                                                </div>

                                                <div style={{ marginTop: '6px' }}>
                                                    <strong>Nota:</strong> a importação foi cancelada e revertida para evitar importação parcial.
                                                </div>
                                            </div>
                                        )}

                                        {(importResult.skippedBySheet && importResult.skippedBySheet.length > 0) ? (
                                            <div
                                                style={{
                                                    marginTop: '12px',
                                                    padding: '10px',
                                                    background: '#fff5f5',
                                                    border: '1px solid #f5c2c7',
                                                    borderRadius: '4px',
                                                    textAlign: 'left',
                                                    color: '#842029',
                                                    fontSize: '14px',
                                                    maxHeight: '145px',
                                                    overflowY: 'auto'
                                                }}
                                            >
                                                <strong>Erros encontrados:</strong>

                                                <div style={styles.importErrorsList}>
                                                    {importResult.skippedBySheet.map((sheet, sheetIndex) => (
                                                        <div key={sheetIndex} style={styles.importErrorSheet}>
                                                            <div style={styles.importErrorSheetTitle}>
                                                                {sheetIndex + 1}. Folha "{sheet.sheetName}" — {sheet.errorCount} erro(s):
                                                            </div>

                                                            <ul style={styles.importErrorSublist}>
                                                                {sheet.errors.map((err, errIndex) => {
                                                                    const location = err.cell
                                                                        ? `célula ${err.cell}`
                                                                        : (
                                                                            err.row !== null && err.row !== undefined &&
                                                                                err.col !== null && err.col !== undefined
                                                                                ? `linha ${err.row}, coluna ${err.col}`
                                                                                : 'local não identificado'
                                                                        );

                                                                    return (
                                                                        <li key={errIndex} style={styles.importErrorSublistItem}>
                                                                            {location}: {err.error}
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            importResult.skippedSummary && importResult.skippedSummary.length > 0 && (
                                                <div
                                                    style={{
                                                        marginTop: '12px',
                                                        padding: '10px',
                                                        background: '#fff5f5',
                                                        border: '1px solid #f5c2c7',
                                                        borderRadius: '4px',
                                                        textAlign: 'left',
                                                        color: '#842029',
                                                        fontSize: '14px',
                                                        maxHeight: '145px',
                                                        overflowY: 'auto'
                                                    }}
                                                >
                                                    <strong>Erros encontrados:</strong>

                                                    <ul style={styles.importErrorSublist}>
                                                        {importResult.skippedSummary.map((err, index) => (
                                                            <li key={index} style={styles.importErrorSublistItem}>
                                                                {err}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )
                                        )}

                                        {(!importResult.skippedBySheet || importResult.skippedBySheet.length === 0) &&
                                            (!importResult.skippedSummary || importResult.skippedSummary.length === 0) &&
                                            (importResult.sheetName || importResult.row || importResult.col || importResult.cell) && (
                                                <div
                                                    style={{
                                                        marginTop: '12px',
                                                        padding: '10px',
                                                        background: '#fff5f5',
                                                        border: '1px solid #f5c2c7',
                                                        borderRadius: '4px',
                                                        textAlign: 'left',
                                                        color: '#842029',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    {importResult.sheetName && (
                                                        <div>
                                                            <strong>Sheet:</strong> {importResult.sheetName}
                                                        </div>
                                                    )}

                                                    {importResult.row !== null && importResult.row !== undefined && (
                                                        <div>
                                                            <strong>Linha:</strong> {importResult.row}
                                                        </div>
                                                    )}

                                                    {importResult.col !== null && importResult.col !== undefined && (
                                                        <div>
                                                            <strong>Coluna:</strong> {importResult.col}
                                                        </div>
                                                    )}

                                                    {importResult.cell && (
                                                        <div>
                                                            <strong>Célula:</strong> {importResult.cell}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                        {importResult.warnings && importResult.warnings.length > 0 && (
                                            <div
                                                style={{
                                                    marginTop: '12px',
                                                    padding: '10px',
                                                    background: '#fff3cd',
                                                    border: '1px solid #ffeeba',
                                                    borderRadius: '4px',
                                                    textAlign: 'left',
                                                    color: '#856404',
                                                    fontSize: '13px',
                                                    maxHeight: '90px',
                                                    overflowY: 'auto'
                                                }}
                                            >
                                                <strong>⚠️ Avisos encontrados antes do erro:</strong>

                                                <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                                                    {importResult.warnings.map((msg, index) => (
                                                        <li key={index} style={{ marginBottom: '6px' }}>
                                                            {msg}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div
                                    style={{
                                        position: 'sticky',
                                        bottom: 0,
                                        marginTop: '10px',
                                        paddingTop: '10px',
                                        paddingBottom: '4px',
                                        background: '#fff',
                                        textAlign: 'center',
                                        borderTop: '1px solid #eee'
                                    }}
                                >
                                    <button style={styles.footerBtn} onClick={closeImportModal}>
                                        Fechar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showReplaceModal && replaceQuadroTarget && (
                <div style={styles.modalOverlay}>
                    <div style={{ ...styles.modalContent, maxWidth: '600px' }}>
                        <h2 style={{ ...styles.modalTitle, borderBottom: '2px solid #E67E22' }}>
                            Substituir Quadro
                        </h2>

                        {!replaceResult ? (
                            <div style={{ padding: '15px' }}>
                                {/* EXIBIÇÃO DA HIERARQUIA */}
                                <div
                                    style={{
                                        marginBottom: '15px',
                                        padding: '8px 12px',
                                        background: '#f8f9fa',
                                        borderRadius: '4px',
                                        borderLeft: '4px solid #E67E22',
                                        fontSize: '13px',
                                        color: '#555'
                                    }}
                                >
                                    <strong>📍 Localização Atual:</strong>
                                    <br />
                                    <span style={{ color: '#E67E22', fontWeight: 'bold' }}>
                                        {getQuadroPath(replaceQuadroTarget)}
                                    </span>
                                </div>

                                <div
                                    style={{
                                        marginBottom: 15,
                                        background: '#fff3cd',
                                        padding: 10,
                                        borderRadius: 4,
                                        color: '#856404',
                                        fontSize: '14px'
                                    }}
                                >
                                    <strong>Atenção:</strong> Esta ação irá apagar todos os dados atuais do quadro
                                    <strong> {replaceQuadroTarget.nome}</strong> e substituí-los pelo conteúdo da folha Excel indicada.
                                </div>

                                <div
                                    style={styles.dropZone}
                                    onClick={() => document.getElementById('replace-excel-upload').click()}
                                >
                                    <FaExchangeAlt
                                        style={{
                                            color: '#E67E22',
                                            fontSize: '40px',
                                            marginBottom: '10px'
                                        }}
                                    />

                                    <p
                                        style={{
                                            fontSize: '14px',
                                            color: '#E67E22',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {replaceFile ? replaceFile.name : "Clique para selecionar o ficheiro Excel"}
                                    </p>

                                    <input
                                        type="file"
                                        id="replace-excel-upload"
                                        style={{ display: 'none' }}
                                        accept=".xlsx, .xls"
                                        onChange={(e) => setReplaceFile(e.target.files[0])}
                                    />
                                </div>

                                <div
                                    style={{
                                        marginTop: 15,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <label
                                        style={{
                                            marginRight: 10,
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                            color: '#333'
                                        }}
                                    >
                                        Nº da Worksheet (Aba):
                                    </label>

                                    <input
                                        type="number"
                                        min="1"
                                        value={replaceSheetIndex}
                                        onChange={(e) => setReplaceSheetIndex(parseInt(e.target.value, 10) || 1)}
                                        style={{
                                            padding: 5,
                                            width: 60,
                                            textAlign: 'center',
                                            border: '1px solid #ccc',
                                            color: '#333'
                                        }}
                                    />
                                </div>

                                <div style={styles.modalFooter}>
                                    <button
                                        style={{
                                            ...styles.footerBtn,
                                            background: replaceFile ? '#E67E22' : '#ccc',
                                            color: '#fff',
                                            border: 'none'
                                        }}
                                        disabled={!replaceFile}
                                        onClick={handleReplaceSubmit}
                                    >
                                        Substituir
                                    </button>

                                    <button
                                        style={styles.footerBtn}
                                        onClick={() => setShowReplaceModal(false)}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div
                                style={{
                                    padding: '16px',
                                    textAlign: 'center',
                                    maxHeight: '72vh',
                                    overflowY: 'auto',
                                    paddingBottom: '70px'
                                }}
                            >
                                {replaceResult.success ? (
                                    <div style={{ color: 'green', fontSize: '16px' }}>
                                        <p style={{ fontSize: '40px' }}>✅</p>
                                        <strong>Substituição Concluída!</strong>
                                        <p>{replaceResult.message}</p>

                                        {replaceResult.warnings && replaceResult.warnings.length > 0 && (
                                            <div
                                                style={{
                                                    marginTop: '15px',
                                                    padding: '10px',
                                                    background: '#fff3cd',
                                                    color: '#856404',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ffeeba',
                                                    fontSize: '14px',
                                                    textAlign: 'left'
                                                }}
                                            >
                                                <strong>⚠️ Avisos na importação:</strong>

                                                <ul style={{ margin: '5px 0 0 20px', padding: 0 }}>
                                                    {replaceResult.warnings.map((w, i) => (
                                                        <li key={i}>{w}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ color: '#dc3545', fontSize: '16px' }}>
                                        <p style={{ fontSize: '40px' }}>❌</p>
                                        <strong>Erro na Substituição</strong>

                                        <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {replaceResult.message}
                                        </p>

                                        {replaceResult.skippedBySheet && replaceResult.skippedBySheet.length > 0 && (
                                            <div
                                                style={{
                                                    marginTop: '12px',
                                                    padding: '10px',
                                                    background: '#fff5f5',
                                                    border: '1px solid #f5c2c7',
                                                    borderRadius: '4px',
                                                    textAlign: 'left',
                                                    color: '#842029',
                                                    fontSize: '14px',
                                                    maxHeight: '260px',
                                                    overflowY: 'auto'
                                                }}
                                            >
                                                <strong>Erros encontrados:</strong>

                                                <div style={{ marginTop: '8px' }}>
                                                    {replaceResult.skippedBySheet.map((sheet, sheetIndex) => (
                                                        <div key={sheetIndex} style={{ marginBottom: '10px' }}>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                {sheetIndex + 1}. Folha "{sheet.sheetName}" — {sheet.errorCount} erro(s):
                                                            </div>

                                                            <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                                                                {sheet.errors.map((err, errIndex) => {
                                                                    const location = err.cell
                                                                        ? `célula ${err.cell}`
                                                                        : err.row && err.col
                                                                            ? `linha ${err.row}, coluna ${err.col}`
                                                                            : 'local não identificado';

                                                                    return (
                                                                        <li key={errIndex} style={{ marginBottom: '4px' }}>
                                                                            {location}: {err.error}
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {(!replaceResult.skippedBySheet || replaceResult.skippedBySheet.length === 0) &&
                                            replaceResult.skippedSummary &&
                                            replaceResult.skippedSummary.length > 0 && (
                                                <div
                                                    style={{
                                                        marginTop: '12px',
                                                        padding: '10px',
                                                        background: '#fff5f5',
                                                        border: '1px solid #f5c2c7',
                                                        borderRadius: '4px',
                                                        textAlign: 'left',
                                                        color: '#842029',
                                                        fontSize: '14px',
                                                        maxHeight: '260px',
                                                        overflowY: 'auto'
                                                    }}
                                                >
                                                    <strong>Erros encontrados:</strong>

                                                    <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                                                        {replaceResult.skippedSummary.map((err, index) => (
                                                            <li key={index} style={{ marginBottom: '4px' }}>
                                                                {err}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                        {(!replaceResult.skippedBySheet || replaceResult.skippedBySheet.length === 0) &&
                                            (!replaceResult.skippedSummary || replaceResult.skippedSummary.length === 0) &&
                                            (replaceResult.sheetName || replaceResult.row || replaceResult.col || replaceResult.cell) && (
                                                <div
                                                    style={{
                                                        marginTop: '12px',
                                                        padding: '10px',
                                                        background: '#fff5f5',
                                                        border: '1px solid #f5c2c7',
                                                        borderRadius: '4px',
                                                        textAlign: 'left',
                                                        color: '#842029',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    {replaceResult.sheetName && (
                                                        <div>
                                                            <strong>Sheet:</strong> {replaceResult.sheetName}
                                                        </div>
                                                    )}

                                                    {replaceResult.row && (
                                                        <div>
                                                            <strong>Linha:</strong> {replaceResult.row}
                                                        </div>
                                                    )}

                                                    {replaceResult.col && (
                                                        <div>
                                                            <strong>Coluna:</strong> {replaceResult.col}
                                                        </div>
                                                    )}

                                                    {replaceResult.cell && (
                                                        <div>
                                                            <strong>Célula:</strong> {replaceResult.cell}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                    </div>
                                )}

                                <div style={{ marginTop: '20px' }}>
                                    <button style={styles.footerBtn} onClick={closeReplaceModal}>
                                        Fechar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {showCreateImportResultModal && createImportResult && (
                <div style={{ ...styles.modalOverlay, zIndex: 2400 }}>
                    <div style={{ ...styles.modalContent, maxWidth: '700px' }}>
                        <h2
                            style={{
                                ...styles.modalTitle,
                                borderBottom: createImportResult.success
                                    ? '2px solid #28a745'
                                    : '2px solid #dc3545'
                            }}
                        >
                            Resultado da Criação do Quadro
                        </h2>

                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            {createImportResult.success ? (
                                <div style={{ color: 'green', fontSize: '16px' }}>
                                    <p style={{ fontSize: '40px' }}>✅</p>
                                    <strong>Quadro criado e Excel importado</strong>
                                </div>
                            ) : (
                                <div style={{ color: '#dc3545', fontSize: '16px' }}>
                                    <p style={{ fontSize: '40px' }}>⚠️</p>
                                    <strong>Quadro criado, mas Excel com erros</strong>
                                </div>
                            )}

                            <div
                                style={{
                                    marginTop: '15px',
                                    padding: '10px',
                                    background: '#d1e7dd',
                                    border: '1px solid #badbcc',
                                    borderRadius: '4px',
                                    color: '#0f5132',
                                    fontSize: '14px',
                                    textAlign: 'left'
                                }}
                            >
                                <strong>✅ Quadro criado com sucesso:</strong>{' '}
                                {createImportResult.quadroNome}
                            </div>

                            <p
                                style={{
                                    marginTop: '15px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    color: createImportResult.success ? '#0f5132' : '#842029',
                                    fontSize: '14px'
                                }}
                            >
                                {createImportResult.message}
                            </p>

                            {createImportResult.detailMessage && (
                                <p
                                    style={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        color: '#842029',
                                        fontSize: '14px'
                                    }}
                                >
                                    {createImportResult.detailMessage}
                                </p>
                            )}

                            {createImportResult.warnings && createImportResult.warnings.length > 0 && (
                                <div
                                    style={{
                                        marginTop: '12px',
                                        padding: '10px',
                                        background: '#fff3cd',
                                        border: '1px solid #ffeeba',
                                        borderRadius: '4px',
                                        textAlign: 'left',
                                        color: '#856404',
                                        fontSize: '13px',
                                        maxHeight: '180px',
                                        overflowY: 'auto'
                                    }}
                                >
                                    <strong>⚠️ Avisos:</strong>

                                    <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                                        {createImportResult.warnings.map((msg, index) => (
                                            <li key={index} style={{ marginBottom: '6px' }}>
                                                {msg}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {createImportResult.skippedBySheet && createImportResult.skippedBySheet.length > 0 && (
                                <div
                                    style={{
                                        marginTop: '12px',
                                        padding: '10px',
                                        background: '#fff5f5',
                                        border: '1px solid #f5c2c7',
                                        borderRadius: '4px',
                                        textAlign: 'left',
                                        color: '#842029',
                                        fontSize: '14px',
                                        maxHeight: '260px',
                                        overflowY: 'auto'
                                    }}
                                >
                                    <strong>Erros encontrados no Excel:</strong>

                                    <div style={styles.importErrorsList}>
                                        {createImportResult.skippedBySheet.map((sheet, sheetIndex) => (
                                            <div key={sheetIndex} style={styles.importErrorSheet}>
                                                <div style={styles.importErrorSheetTitle}>
                                                    {sheetIndex + 1}. Folha "{sheet.sheetName}" — {sheet.errorCount} erro(s):
                                                </div>

                                                <ul style={styles.importErrorSublist}>
                                                    {sheet.errors.map((err, errIndex) => {
                                                        const location = err.cell
                                                            ? `célula ${err.cell}`
                                                            : (
                                                                err.row !== null && err.row !== undefined &&
                                                                    err.col !== null && err.col !== undefined
                                                                    ? `linha ${err.row}, coluna ${err.col}`
                                                                    : 'local não identificado'
                                                            );

                                                        return (
                                                            <li key={errIndex} style={styles.importErrorSublistItem}>
                                                                {location}: {err.error}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(!createImportResult.skippedBySheet || createImportResult.skippedBySheet.length === 0) &&
                                createImportResult.skippedSummary &&
                                createImportResult.skippedSummary.length > 0 && (
                                    <div
                                        style={{
                                            marginTop: '12px',
                                            padding: '10px',
                                            background: '#fff5f5',
                                            border: '1px solid #f5c2c7',
                                            borderRadius: '4px',
                                            textAlign: 'left',
                                            color: '#842029',
                                            fontSize: '14px',
                                            maxHeight: '260px',
                                            overflowY: 'auto'
                                        }}
                                    >
                                        <strong>Erros encontrados no Excel:</strong>

                                        <ul style={styles.importErrorSublist}>
                                            {createImportResult.skippedSummary.map((err, index) => (
                                                <li key={index} style={styles.importErrorSublistItem}>
                                                    {err}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                            {(!createImportResult.skippedBySheet || createImportResult.skippedBySheet.length === 0) &&
                                (!createImportResult.skippedSummary || createImportResult.skippedSummary.length === 0) &&
                                (createImportResult.sheetName ||
                                    createImportResult.row ||
                                    createImportResult.col ||
                                    createImportResult.cell) && (
                                    <div
                                        style={{
                                            marginTop: '12px',
                                            padding: '10px',
                                            background: '#fff5f5',
                                            border: '1px solid #f5c2c7',
                                            borderRadius: '4px',
                                            textAlign: 'left',
                                            color: '#842029',
                                            fontSize: '14px'
                                        }}
                                    >
                                        {createImportResult.sheetName && (
                                            <div>
                                                <strong>Sheet:</strong> {createImportResult.sheetName}
                                            </div>
                                        )}

                                        {createImportResult.row !== null && createImportResult.row !== undefined && (
                                            <div>
                                                <strong>Linha:</strong> {createImportResult.row}
                                            </div>
                                        )}

                                        {createImportResult.col !== null && createImportResult.col !== undefined && (
                                            <div>
                                                <strong>Coluna:</strong> {createImportResult.col}
                                            </div>
                                        )}

                                        {createImportResult.cell && (
                                            <div>
                                                <strong>Célula:</strong> {createImportResult.cell}
                                            </div>
                                        )}
                                    </div>
                                )}

                            <div style={{ marginTop: '20px' }}>
                                <button
                                    style={styles.footerBtn}
                                    onClick={() => {
                                        setShowCreateImportResultModal(false);
                                        setCreateImportResult(null);
                                        fetchQuadros();
                                    }}
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* --- MODAL: ERRO NA OPERAÇÃO --- */}
            {
                showErrorModal && (
                    <div style={{ ...styles.modalOverlay, zIndex: 2500 }}>
                        <div style={{ ...styles.modalContent, maxWidth: '400px', textAlign: 'center' }}>
                            <div style={{ color: '#dc3545', fontSize: '16px', padding: '15px' }}>
                                <p style={{ fontSize: '40px', margin: '0 0 10px 0' }}>❌</p>
                                <strong>Erro na Operação</strong>
                                <p style={{ color: '#333', marginTop: '10px', fontSize: '14px' }}>{errorMessage}</p>
                            </div>
                            <div style={styles.modalFooterCenter}>
                                <button
                                    style={{ ...styles.footerBtn, background: '#dc3545', color: 'white', border: 'none' }}
                                    onClick={() => setShowErrorModal(false)}
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* --- MODAL: IMPORTAR ZIP HTML (INDIVIDUAL) --- */}
            {
                showZipImportModal && zipImportTarget && (
                    <div style={styles.modalOverlay}>
                        <div style={{ ...styles.modalContent, maxWidth: '600px' }}>
                            <h2 style={{ ...styles.modalTitle, borderBottom: '2px solid #005c75' }}>Importar ZIP (HTML)</h2>
                            {/* EXIBIÇÃO DA HIERARQUIA */}
                            <div style={{ marginBottom: '15px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '4px', borderLeft: '4px solid #005c75', fontSize: '13px', color: '#555', textAlign: 'left' }}>
                                <strong>📍 Localização Atual:</strong><br />
                                <span style={{ color: '#005c75', fontWeight: 'bold' }}>{getQuadroPath(zipImportTarget)}</span>
                            </div>
                            {!zipImportResult ? (
                                <div style={{ padding: '15px' }}>
                                    <p style={{ fontSize: '14px', color: '#333' }}>Importar ficheiro ZIP (HTML) para o quadro: <br /><strong>{zipImportTarget.nome}</strong></p>
                                    <div style={{ ...styles.dropZone, borderColor: '#005c75' }} onClick={() => document.getElementById('single-zip-upload').click()}>
                                        <RiFileZipFill style={{ color: '#005c75', fontSize: '40px', marginBottom: '10px' }} />
                                        <p style={{ fontSize: '14px', color: '#005c75', fontWeight: 'bold' }}>
                                            {zipFile ? zipFile.name : "Clique para selecionar 1 ficheiro .zip"}
                                        </p>
                                        <input type="file" id="single-zip-upload" style={{ display: 'none' }} accept=".zip" onChange={(e) => setZipFile(e.target.files[0])} />
                                    </div>
                                    <div style={styles.modalFooter}>
                                        <button style={{ ...styles.footerBtn, background: zipFile ? '#005c75' : '#ccc', color: '#fff', border: 'none' }} disabled={!zipFile} onClick={handleZipImportSubmit}>Importar</button>
                                        <button style={styles.footerBtn} onClick={() => setShowZipImportModal(false)}>Cancelar</button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ padding: '20px', textAlign: 'center' }}>
                                    {zipImportResult.success ? (
                                        <div style={{ color: 'green', fontSize: '16px' }}>
                                            <p style={{ fontSize: '40px' }}>✅</p><strong>Sucesso!</strong><p>{zipImportResult.message}</p>
                                        </div>
                                    ) : (
                                        <div style={{ color: '#dc3545', fontSize: '16px' }}>
                                            <p style={{ fontSize: '40px' }}>❌</p><strong>Erro</strong><p>{zipImportResult.message}</p>
                                        </div>
                                    )}
                                    <div style={{ marginTop: '20px' }}>
                                        <button style={styles.footerBtn} onClick={() => { setShowZipImportModal(false); fetchQuadros(); }}>Fechar</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )
            }

            {/* --- MODAL: IMPORTAR ZIPs HTML (MÚLTIPLOS NO PAI) --- */}
            {
                showMultiZipImportModal && multiZipImportTarget && (
                    <div style={styles.modalOverlay}>
                        <div style={{ ...styles.modalContent, maxWidth: '600px' }}>
                            <h2 style={{ ...styles.modalTitle, borderBottom: '2px solid #005c75' }}>Importar Múltiplos ZIPs (HTML)</h2>
                            <div style={{ marginBottom: '15px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '4px', borderLeft: '4px solid #005c75', fontSize: '13px', color: '#555', textAlign: 'left' }}>
                                <strong>📍 Localização do Quadro Pai:</strong><br />
                                <span style={{ color: '#005c75', fontWeight: 'bold' }}>{getQuadroPath(multiZipImportTarget)}</span>
                            </div>
                            {!multiZipImportResult ? (
                                <div style={{ padding: '15px' }}>
                                    <p style={{ fontSize: '14px', color: '#333' }}>Importar ficheiros ZIP (HTML) para a hierarquia do quadro: <br /><strong>{multiZipImportTarget.nome}</strong></p>
                                    <div style={{ ...styles.dropZone, borderColor: '#005c75' }} onClick={() => document.getElementById('multi-zip-upload').click()}>
                                        <RiFileZipFill style={{ color: '#005c75', fontSize: '40px', marginBottom: '10px' }} />
                                        <p style={{ fontSize: '14px', color: '#005c75', fontWeight: 'bold' }}>
                                            {multiZipFiles?.length > 0 ? `${multiZipFiles.length} ficheiro(s) ZIP selecionado(s)` : "Clique para selecionar VÁRIOS ficheiros .zip"}
                                        </p>
                                        <input type="file" id="multi-zip-upload" multiple style={{ display: 'none' }} accept=".zip" onChange={(e) => setMultiZipFiles(Array.from(e.target.files))} />
                                    </div>

                                    {multiZipFiles?.length > 0 && (
                                        <ul style={{ maxHeight: '100px', overflowY: 'auto', fontSize: '12px', color: '#666', textAlign: 'left', background: '#f5f5f5', padding: '10px 25px', borderRadius: '4px' }}>
                                            {multiZipFiles.map((f, i) => <li key={i}>{f.name}</li>)}
                                        </ul>
                                    )}

                                    <div style={styles.modalFooter}>
                                        <button style={{ ...styles.footerBtn, background: multiZipFiles?.length ? '#005c75' : '#ccc', color: '#fff', border: 'none' }} disabled={!multiZipFiles?.length} onClick={handleMultiZipImportSubmit}>Importar Múltiplos</button>
                                        <button style={styles.footerBtn} onClick={() => setShowMultiZipImportModal(false)}>Cancelar</button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ padding: '20px', textAlign: 'center' }}>
                                    {multiZipImportResult.success ? (
                                        <div style={{ color: 'green', fontSize: '16px' }}>
                                            <p style={{ fontSize: '40px' }}>✅</p><strong>Sucesso!</strong><p>{multiZipImportResult.message}</p>
                                        </div>
                                    ) : (
                                        <div style={{ color: '#dc3545', fontSize: '16px' }}>
                                            <p style={{ fontSize: '40px' }}>❌</p><strong>Erro</strong><p>{multiZipImportResult.message}</p>
                                        </div>
                                    )}
                                    <div style={{ marginTop: '20px' }}>
                                        <button style={styles.footerBtn} onClick={() => { setShowMultiZipImportModal(false); fetchQuadros(); }}>Fechar</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )
            }
            {/* --- MODAL: CRIAR QUADRO --- */}
            {
                showQuadroCreate && (
                    <div style={styles.modalOverlay}>
                        <div style={{ ...styles.modalContent, maxWidth: '800px' }}>
                            <h2 style={styles.modalTitle}>Criar Novo Quadro</h2>
                            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                <table style={styles.infoTable}>
                                    <tbody>
                                        <tr>
                                            <td style={styles.infoLabel}>Nome (Obrigatório):</td>
                                            <td style={styles.infoVal}>
                                                <input style={styles.modalInput} value={createQuadroData.nome} onChange={e => setCreateQuadroData({ ...createQuadroData, nome: e.target.value })} placeholder="Nome do Quadro" />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.infoLabel}>Menu:</td>
                                            <td style={styles.infoVal}>
                                                <input style={styles.modalInput} value={createQuadroData.nomeMenu} onChange={e => setCreateQuadroData({ ...createQuadroData, nomeMenu: e.target.value })} placeholder="Nome a apresentar no Menu (Opcional)" />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.infoLabel}>Pai (Agrupador):</td>
                                            <td style={styles.infoVal}>
                                                <select style={styles.modalInput} value={createQuadroData.quadroPai} onChange={e => setCreateQuadroData({ ...createQuadroData, quadroPai: Number(e.target.value) })}>
                                                    <option value={0}>[Sem Pai - Subdiretório Principal]</option>
                                                    {/* Permite escolher apenas quadros do tipo MENU (0) ou os que já têm filhos, para organização */}
                                                    {quadrosData.filter(x => x.tipoDados === 0 || quadrosData.some(child => child.quadroPai === x.id)).map(x => (
                                                        <option key={x.id} value={x.id}>{x.nome}</option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.infoLabel}>Tipo de Dados:</td>
                                            <td style={styles.infoVal}>
                                                <select
                                                    style={styles.modalInput}
                                                    value={createQuadroData.tipoDados}
                                                    onChange={e => {
                                                        const novoTipo = Number(e.target.value);

                                                        setCreateQuadroData({
                                                            ...createQuadroData,
                                                            tipoDados: novoTipo
                                                        });

                                                        // Limpa o ficheiro quando se muda de tipo, para evitar enviar ZIP como Excel ou vice-versa
                                                        setQuadroFile(null);
                                                    }}
                                                >
                                                    <option value={100}>HTML (Ficheiro Local/ZIP)</option>
                                                    <option value={0}>MENU (Apenas Agrupador)</option>
                                                    <option value={1}>TIPO 1 (Tabela Normal)</option>
                                                    <option value={2}>TIPO 2 (Tabela com Média)</option>
                                                    <option value={3}>TIPO 3 (Tabela PV)</option>
                                                </select>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.infoLabel}>Ordem no Menu:</td>
                                            <td style={styles.infoVal}>
                                                <input type="number" style={{ ...styles.modalInput, width: 60 }} value={createQuadroData.ordem} onChange={e => setCreateQuadroData({ ...createQuadroData, ordem: e.target.value })} />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.infoLabel}>Estado:</td>
                                            <td style={styles.infoVal}>
                                                <label style={{ marginRight: 15, color: '#333', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><input type="radio" checked={createQuadroData.estado === 1} onChange={() => setCreateQuadroData({ ...createQuadroData, estado: 1 })} style={{ marginRight: 5 }} /> Activo</label>
                                                <label style={{ color: '#333', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><input type="radio" checked={createQuadroData.estado === 0} onChange={() => setCreateQuadroData({ ...createQuadroData, estado: 0 })} style={{ marginRight: 5 }} /> Inactivo</label>
                                            </td>
                                        </tr>
                                        {createQuadroData.tipoDados === 100 && (
                                            <tr>
                                                <td style={styles.infoLabel}>Ficheiro ZIP (HTML):</td>
                                                <td style={styles.infoVal}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            minHeight: '34px'
                                                        }}
                                                    >
                                                        <label style={{
                                                            ...styles.uploadBtn,
                                                            background: '#C87500',
                                                            color: '#fff',
                                                            border: 'none',
                                                            margin: 0,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            height: '32px',
                                                            lineHeight: 'normal'
                                                        }}>
                                                            <RiFileZipFill style={{ marginRight: 5 }} /> Escolher Ficheiro
                                                            <input
                                                                type="file"
                                                                accept=".zip"
                                                                hidden
                                                                onChange={e => setQuadroFile(e.target.files[0])}
                                                            />
                                                        </label>
                                                        <span style={{
                                                            marginLeft: 10,
                                                            fontSize: 14,
                                                            color: '#333',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            height: '32px',
                                                            lineHeight: 'normal'
                                                        }}
                                                        >
                                                            {quadroFile ? quadroFile.name : "Nenhum ficheiro selecionado"}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}

                                        {[1, 2, 3].includes(createQuadroData.tipoDados) && (
                                            <tr>
                                                <td style={styles.infoLabel}>Ficheiro Excel:</td>
                                                <td style={styles.infoVal}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            minHeight: '34px'
                                                        }}
                                                    >
                                                        <label style={{
                                                            ...styles.uploadBtn,
                                                            background: '#1D6F42',
                                                            color: '#fff',
                                                            border: 'none',
                                                            margin: 0,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            height: '32px',
                                                            lineHeight: 'normal'
                                                        }}>
                                                            <RiFileExcel2Fill style={{ marginRight: 5 }} /> Escolher Excel
                                                            <input
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                hidden
                                                                onChange={e => setQuadroFile(e.target.files[0])}
                                                            />
                                                        </label>

                                                        <span style={{
                                                            marginLeft: 10,
                                                            fontSize: 14,
                                                            color: '#333',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            height: '32px',
                                                            lineHeight: 'normal'
                                                        }}>
                                                            {quadroFile ? quadroFile.name : "Nenhum ficheiro selecionado"}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div style={styles.modalFooter}>
                                <button
                                    style={{ ...styles.footerBtn, background: '#28a745', color: 'white' }}
                                    onClick={saveQuadroCreate}
                                >
                                    Criar Quadro
                                </button>
                                <button style={styles.footerBtn} onClick={() => setShowQuadroCreate(false)}>Cancelar</button>
                            </div>
                        </div>
                    </div>
                )
            }

        </div >
    );
};

export default QuadrosModal;